import type { ClientMessage, ServerMessage } from '@battle/shared';

export interface PingStatus {
  state: 'connecting' | 'online' | 'offline';
  rttMs: number | null;
  server: string | null;
  version: string | null;
}

/**
 * M0 transport check: keeps a WebSocket to /play/<gs> open and measures round-trip time.
 * It exercises the whole path (browser -> Traefik -> Caddy -> game server) that real matches use.
 */
export function connectPing(gs: string, onStatus: (s: PingStatus) => void): () => void {
  const status: PingStatus = { state: 'connecting', rttMs: null, server: null, version: null };
  let ws: WebSocket | null = null;
  let timer: ReturnType<typeof setInterval> | undefined;
  let retry = 1000;
  let stopped = false;

  const url = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/play/${gs}`;
  const emit = () => onStatus({ ...status });

  const open = () => {
    if (stopped) return;
    ws = new WebSocket(url);
    ws.onopen = () => {
      retry = 1000;
      status.state = 'online';
      emit();
      const ping = () =>
        ws?.send(JSON.stringify({ t: 'ping', c: performance.now() } satisfies ClientMessage));
      ping();
      timer = setInterval(ping, 2000);
    };
    ws.onmessage = (e) => {
      const msg = JSON.parse(String(e.data)) as ServerMessage;
      if (msg.t === 'hello') {
        status.server = msg.gs;
        status.version = msg.version;
      } else if (msg.t === 'pong') {
        const rtt = performance.now() - msg.c;
        status.rttMs = status.rttMs === null ? rtt : status.rttMs * 0.7 + rtt * 0.3;
      }
      emit();
    };
    ws.onclose = () => {
      clearInterval(timer);
      status.state = 'offline';
      status.rttMs = null;
      emit();
      if (!stopped) setTimeout(open, retry);
      retry = Math.min(retry * 2, 15000);
    };
  };

  open();
  return () => {
    stopped = true;
    clearInterval(timer);
    ws?.close();
  };
}
